import { registerTranslations } from '../utils/translations';
import loadAppManifest from './manifest-loader';
import loadAppAssets from './assets-loader';
import type { Manifest, MicroAppModule } from '../shared/types';
import type { IEventBus, LogError, OnAction, TrackMethod } from '@shore/types';

// loadApp forwards these straight into the options bag it hands to the
// micro-app's create() — it never invokes them itself — so each is typed to
// the canonical contract for that boundary rather than to loadApp's (nil)
// internal use of it:
//   - trackMethod/onAction/logError use @shore/types' TrackMethod/OnAction/
//     LogError — the same types @shore/types' own ICreateArgs/
//     IMicroServiceTools use to describe exactly this micro-app tool boundary.
//   - eventBus uses IEventBus (loadAppManifest actually calls .emit on it).
//   - container is a React ref's `.current`, hence HTMLElement | null.
//   - modal is an object bag (defaults to `{}`), NOT a DOM element — matches
//     AppLoader's own LoadAppParams.modal: Record<string, unknown>.
//   - apiClient stays `unknown`: the app-shell's real client is
//     AppShellApiClient (src/shared/types), not @shore/types' IApiClient, and
//     loadApp is a pure conduit for it — no value in coupling to either here.
export interface LoadAppOptions {
  name: string;
  container?: HTMLElement | null;
  modal?: Record<string, unknown>;
  useHashHistory?: boolean;
  routingType?: string;
  pathAndQuery?: string;
  apiClient?: unknown;
  trackMethod?: TrackMethod;
  onAction?: OnAction;
  eventBus?: IEventBus;
  settings: { locale: string; [key: string]: unknown };
  logError?: LogError;
}

interface LoadAppResult {
  manifest: Manifest;
  create: () => ReturnType<MicroAppModule['create']>;
}

export default async function loadApp({
  name,
  container,
  modal,
  useHashHistory,
  routingType,
  pathAndQuery,
  apiClient,
  trackMethod,
  onAction,
  eventBus,
  settings, // see example in ../../README.md
  logError,
}: LoadAppOptions): Promise<LoadAppResult> {
  const manifest = await loadAppManifest(name, eventBus);
  const options = {
    ...manifest.additionalCreateOptions,
    container,
    modal,
    useHashHistory,
    routingType,
    pathAndQuery,
    apiClient,
    trackMethod,
    onAction,
    settings,
    mountPath: `/${name}`,
    logError,
    eventBus,
  };
  const { App, translationDataList } = await loadAppAssets(
    name,
    manifest,
    settings.locale
  );

  translationDataList.forEach(({ namespace, data }) => {
    registerTranslations(namespace, data);
  });

  return {
    manifest,
    create: () => App.create(options),
  };
}
